/**
 * The header badge: how much of your work is still on this device.
 *
 * In the header rather than on a page, for the same reason the flush lives in
 * main.tsx — whoever queued the work is usually not on the page that queued
 * it, and often is not the person who queued it at all. It is visible wherever
 * they are, which is the whole point of the thing.
 *
 * IT HIDES WHEN THERE IS NOTHING. A permanent icon that is grey 99% of the
 * time is furniture, and furniture is not read. This appears only when there
 * is something to say, which is what makes it worth looking at when it does.
 *
 * The kiosk gets PendingQueueLine instead — it renders no header at all.
 */

import { useState } from 'react';
import { CloudArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PendingQueuePanel from '@/components/Common/PendingQueuePanel';
import { usePendingQueue } from '@/components/Common/usePendingQueue';

export default function PendingQueueBadge() {
  const { waiting, blocked } = usePendingQueue();
  const [open, setOpen] = useState(false);
  const total = waiting.length + blocked.length;

  if (total === 0) return null;

  // Anything blocked makes the whole badge red, even beside nine that are
  // merely waiting: the waiting ones resolve themselves and the blocked one
  // does not, so it is the one that decides whether this needs a person.
  const needsPerson = blocked.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="pending-queue-badge"
        className={`relative flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
          needsPerson
            ? 'bg-danger-50 text-danger-700 hover:bg-danger-100 dark:bg-danger-950 dark:text-danger-200'
            : 'bg-warning-50 text-warning-800 hover:bg-warning-100 dark:bg-warning-950 dark:text-warning-200'
        }`}
        aria-label={
          needsPerson
            ? `${total} unsent ${total === 1 ? 'item' : 'items'} on this device, ` +
              `${blocked.length} needing attention`
            : `${total} unsent ${total === 1 ? 'item' : 'items'} on this device`
        }
        data-no-print
      >
        {needsPerson
          ? <ExclamationTriangleIcon className="h-4 w-4" />
          : <CloudArrowUpIcon className="h-4 w-4" />}
        <span>{total}</span>
        {/* The word matters on a first read; the number alone reads as a
            notification count, which is the one thing this is not. */}
        <span className="hidden sm:inline font-medium">
          {needsPerson ? 'need attention' : 'not sent'}
        </span>
      </button>

      <PendingQueuePanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}
